import { AddressDetailModel } from '@aglenergy/design-system/dist/collection/components/composite/addresssearch/address.model';
import { SessionStorageKeys } from '@aglenergy/shared-library';
import { store } from 'app/shared/store/store';
import { nbnProductSearchApiService } from 'app/nbn/services/nbn-product-search-api/nbn-product-search-api.service';
import { NbnProductsResponse } from 'app/nbn/models/nbn-products-response.model';
import {
  NbnServiceabilityFail,
  ProductCatalogueProduct,
  ProductCategory,
  ProductSearchRequestModel,
  ProductsResponse,
  ReportErrorType,
  ResponseError
} from 'app/shared/models/index';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { addressService } from 'app/shared/services/address/address.service';
import { errorReportingService } from 'app/shared/services/error-reporting/error-reporting.service';
import { mapper } from 'app/shared/mappers/mapper';
import { productsSessionStorageService } from 'app/shared/services/product-session-storage/product-session-storage.service';

class NbnProductService {
  private productCategory: ProductCategory = 'nbn';
  private modemProductName = 'AGL supplied modem';

  private getProductSearchRequest(address?: AddressDetailModel): ProductSearchRequestModel {
    const couponCode = store.get('couponCode') ?? null;
    if (address) {
      const productSearchRequest = mapper.mapProductSearchRequestModel(address);
      if (couponCode) {
        productSearchRequest.couponCode = couponCode;
      }
      return productSearchRequest;
    }

    const defaultProductSearchRequest: ProductSearchRequestModel = {
      couponCode,
      webCampaignId: store.get(StoreKeys.webId) ?? null,
      state: 'All'
    };
    return defaultProductSearchRequest;
  }

  private async getDefaultProducts(): Promise<NbnProductsResponse> {
    const storageProductSessionData = productsSessionStorageService.getLatestProducts<NbnProductsResponse>(this.productCategory);

    if (!storageProductSessionData) {
      return await nbnProductSearchApiService.getProducts(this.getProductSearchRequest()).then((response) => {
        if (this.IsNBNAvailable(response)) {
          productsSessionStorageService.upsertProducts(response, this.productCategory);
          return response;
        }
      });
    }
    return storageProductSessionData.response;
  }

  private async getProductsByAddress(address: AddressDetailModel): Promise<NbnProductsResponse> {
    if (store.get(StoreKeys.aemCIFIntegrationEnabled) && addressService.convertAddressToString(address)?.length > 0) {
      window.postMessage(
        {
          eventType: 'address-validation-status',
          spaCategory: store.get('productCategory'),
          eventData: {
            addressValidation: 'started',
            isAddressPresent: addressService.convertAddressToString(store.get('address'))?.length > 0
          }
        },
        '*'
      );
    }
    // Get response from Storage
    const storageProductSessionData = productsSessionStorageService.getLatestProducts<NbnProductsResponse>(this.productCategory, address);

    // Existing response in Storage
    if (storageProductSessionData) {
      productsSessionStorageService.upsertProducts(
        storageProductSessionData.response,
        storageProductSessionData.productCategory,
        storageProductSessionData.address
      );
      return storageProductSessionData.response;
    }

    // New address
    return await nbnProductSearchApiService.getProducts(this.getProductSearchRequest(address)).then((response) => {
      const isNbnAvailable = this.IsNBNAvailable(response);
      if (isNbnAvailable) {
        productsSessionStorageService.upsertProducts(response, this.productCategory, address);
        return response;
      }
    });
  }

  private IsProductAvailable(productDetails: ProductsResponse, productCategory: ProductCategory): boolean {
    const modemProduct =
      productCategory === 'modem' &&
      productDetails?.products?.find((item) => item.name.toLowerCase().includes(this.modemProductName.toLowerCase()));

    if (
      productDetails.siteError ||
      !productDetails?.products?.length ||
      (productCategory === 'modem' && (!modemProduct || modemProduct.attributes['agl_marketing_price'] === undefined))
    ) {
      throw new ResponseError(`${productCategory.toUpperCase()} not available.`, 'serviceNotAvailable');
    }
    return true;
  }

  private isHfcClass(serviceClass: string, serviceType: string): boolean {
    const hfcClasses = ['20'];
    return hfcClasses.some((item) => item === serviceClass) && serviceType === 'HFC';
  }

  private hasNoQualifiedService(productDetails: NbnProductsResponse): boolean {
    if (productDetails.serviceQualification) {
      return !productDetails.products?.some((product) => {
        return product.content && product.qualified;
      });
    }
    return false;
  }

  public IsNBNAvailable(productDetails: NbnProductsResponse): boolean {
    if (!productDetails) {
      return false;
    }
    if (this.hasNoService(productDetails)) {
      errorReportingService.logError(NbnServiceabilityFail.serviceTypeUnavailableSatellite, ReportErrorType.NbnServiceabilityFailed);
      throw new ResponseError('Service type is unavailable.', 'serviceNotAvailable');
    }

    if (this.hasNoQualifiedService(productDetails)) {
      errorReportingService.logError(NbnServiceabilityFail.addressUnserviceable, ReportErrorType.NbnServiceabilityFailed);
      throw new ResponseError('NBN not available.', 'serviceNotAvailable');
    }

    // This feature flag is added to disable HFC-specific error message. To get it back, just remove the feature flag.
    const featureFlagHfcError = false;
    if (productDetails.siteError) {
      if (
        featureFlagHfcError &&
        this.isHfcClass(productDetails.serviceQualification?.nbn?.serviceClass, productDetails.serviceQualification?.nbn?.serviceType)
      ) {
        throw new ResponseError('NBN not available.', 'hfcServiceNotAvailable');
      }
      throw new ResponseError('NBN not available.', 'serviceNotAvailable');
    }
    return true;
  }

  public hasNoService(productDetails: NbnProductsResponse): boolean {
    if (productDetails?.serviceQualification?.nbn?.serviceType?.toLowerCase() === 'satellite') {
      return true;
    }
    return false;
  }

  public async getProducts(address?: AddressDetailModel): Promise<NbnProductsResponse> {
    if (address) {
      return await this.getProductsByAddress(address);
    }
    return await this.getDefaultProducts();
  }

  public async getProductsByLocationId(locationId: string): Promise<NbnProductsResponse> {
    // Get response from Storage
    const storageProductSessionData = productsSessionStorageService.getNbnProductsByLocationId<NbnProductsResponse>(locationId);

    // Existing response in Storage
    if (storageProductSessionData) {
      productsSessionStorageService.upsertProducts(
        storageProductSessionData.response,
        storageProductSessionData.productCategory,
        storageProductSessionData.address
      );
      return storageProductSessionData.response;
    }

    // New address
    return await nbnProductSearchApiService.getProductsByLocationId(locationId).then((response) => {
      const isNbnAvailable = this.IsNBNAvailable(response);
      if (isNbnAvailable) {
        productsSessionStorageService.upsertProducts(response, this.productCategory, locationId);
        return response;
      }
    });
  }

  public async getVoipProducts(state?: string): Promise<ProductsResponse> {
    const storageProductSessionData = sessionStorage.getItem(SessionStorageKeys.VoipProductDetailsSession);

    if (!storageProductSessionData) {
      if (!state) {
        state = 'All';
      }
      return await nbnProductSearchApiService.getVoipProduct(state).then((response) => {
        if (this.IsProductAvailable(response, 'voip')) {
          sessionStorage.setItem(SessionStorageKeys.VoipProductDetailsSession, JSON.stringify(response));
          return response;
        }
      });
    }
    return await JSON.parse(storageProductSessionData);
  }

  public async getModemProducts(state?: string): Promise<ProductsResponse> {
    const storageProductSessionData = sessionStorage.getItem(SessionStorageKeys.ModemProductDetailsSession);

    if (!storageProductSessionData) {
      if (!state) {
        state = 'All';
      }
      return await nbnProductSearchApiService.getModemProduct(state).then((response) => {
        if (this.IsProductAvailable(response, 'modem')) {
          sessionStorage.setItem(SessionStorageKeys.ModemProductDetailsSession, JSON.stringify(response));
          return response;
        }
      });
    }
    return await JSON.parse(storageProductSessionData);
  }

  public getValidProduct(productCategory: ProductCategory, productDetails: ProductsResponse): ProductCatalogueProduct {
    switch (productCategory) {
      case 'voip':
        return productDetails?.products[0];
      case 'modem':
        return productDetails?.products?.find((item) => item.name.toLowerCase() === this.modemProductName.toLowerCase());
      default:
        break;
    }
    return null;
  }

  public isServiceNotAvailable = (responseError: ResponseError): boolean => {
    return responseError?.errorType === 'serviceNotAvailable' || responseError?.errorType === 'multipleMatching';
  };

  public isHfcServiceNotAvailable = (responseError: ResponseError): boolean => {
    return responseError?.errorType === 'hfcServiceNotAvailable';
  };
}

export const nbnProductService = new NbnProductService();
